/**
 * @fileoverview Confirm-target-payment mutation hook.
 * @description Wraps POST /sales/targets/:id/confirm-payment in a TanStack mutation, invalidating
 * both the owning tenant's target query and its tenant queries (status/onboarding/subscription all
 * change) on success. Intended for Super Admin only — the real backend must enforce this via auth;
 * see targets.service.ts's confirmTargetPayment doc comment.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TARGETS_QUERY_KEY } from '@/features/sales/hooks/useTargets'
import { TENANTS_QUERY_KEY } from '@/features/sales/hooks/useTenants'
import { confirmTargetPayment } from '@/features/sales/services/targets.service'

/**
 * @description Confirms initial payment, completing the target and converting its tenant to Active
 * Client, then refreshes the affected queries.
 * @param {string} tenantId - The tenant the target belongs to, for query invalidation.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof confirmTargetPayment>>,
 * unknown, { targetId: string; confirmedByUserId: string }>>} The mutation object (mutate with `{
 * targetId, confirmedByUserId }`, isPending, ...).
 */
export function useConfirmTargetPayment(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      targetId,
      confirmedByUserId,
    }: {
      targetId: string
      confirmedByUserId: string
    }) => confirmTargetPayment(targetId, confirmedByUserId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...TARGETS_QUERY_KEY, 'byTenant', tenantId] })
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
  })
}
